import { Formik } from "formik"
import { generateDynamicValidations } from "helpers/validationSchema"
import React, { useEffect, useRef } from "react"
import MenuItemsForm from "./components/MenuItemForm"
import { useDispatch, useSelector } from "react-redux"
import { marketingActions } from "redux/marketing/slice/user"
import {
  addBarMenuItemsAction,
  addMenuItemsActions,
  addWineMenuItemsAction,
  updateBarItemsAction,
  updateFoodItemsAction,
  updateWineItemsAction
} from "redux/marketing/actions"
import moment from "moment"
import { getDynamicMenuFieldsAction } from "redux/superAdmin/actions"
import { getDidYouKnowPayload, getHistoryPayload, toTitleCase } from "helpers/functions"

const menuText = {
  BARMENU: "bar menu",
  WINEBAR: "wine by glass",
  WINEBOTTLE: "wine by bottle",
  DINNER: "dinner",
  LUNCH: "lunch",
  BRUNCH: "brunch",
  BREAKFAST: "breakfast",
  SPECIALS: "specials"
}

const history_initial_values = {
  heading: "",
  content: ""
}

function getMenuDescription(desc) {
  let items = []
  if (!desc || typeof desc !== "string") return []
  items = desc?.split(",")
  return items
}

function getAllergence(allergence) {
  let allergenceItems = []
  if (!allergence) return []
  for (let al of allergence) {
    allergenceItems.push(al?.name ? al.name : al)
  }
  return allergenceItems
}

function getTestingNotes(testing) {
  let testingItems = []
  if (!testing) return []
  for (let al of testing) {
    testingItems.push(al?.name ? al.name : al)
  }
  return testingItems
}

function AddMenuItems({
  open = false,
  onClose = () => {},
  isEdit = false,
  isReviewMode = false,
  categoryId = null
}) {
  const { menu_Category_ID, linkCategory, menuItemImageId, foodItem, bar_category } = useSelector(
    (store) => store?.marketing?.menu ?? {}
  )
  const { didYouKnowImage, soundbites } = useSelector((store) => store?.marketing ?? {})
  const dispatch = useDispatch()
  const zone_name = moment.tz.guess()
  // Coerce to an array: the API can return `menuFields` as a non-array object for
  // some categories, and the many menuFields.reduce/.forEach calls below (plus the
  // dynamic validation schema) would otherwise throw "reduce is not a function".
  const menuFieldsRaw = useSelector((store) => store?.superAdmin?.menuFields)
  const menuFields = Array.isArray(menuFieldsRaw) ? menuFieldsRaw : []

  // One-shot success callback for the current save. When an AI generate/refine
  // is requested we first save the item (so the backend has the latest field
  // data) and, on save success, run the AI call instead of closing the form.
  const afterSaveRef = useRef(null)

  // Builds the save dispatch options. For an AI save (afterSaveRef set) the
  // success callback runs the AI call instead of closing, and `keepDetail`
  // tells the saga NOT to clear/replace the open item's detail (the update API
  // returns only a message, which would otherwise blank the still-open form).
  const buildSaveOpts = () => {
    const keepDetail = !!afterSaveRef.current
    const onCloseCb = afterSaveRef.current || onClose
    afterSaveRef.current = null
    return { onClose: onCloseCb, keepDetail }
  }

  // Save the current item, then run `onSaved` once the save succeeds. Validates
  // first so we never persist (or generate from) invalid data.
  const saveBeforeAi = async (formik, onSaved) => {
    const errors = await formik.validateForm()
    if (errors && Object.keys(errors).length > 0) {
      formik.handleSubmit() // surfaces validation errors; does not submit
      return
    }
    afterSaveRef.current = onSaved
    await formik.submitForm()
  }

  function initDateConvert(date, time) {
    const initDate = moment(date).format("ddd MMM DD YYYY") + " " + time
    const temp = new Date(initDate)
    const convertedUTCDate = new Date(
      Date.UTC(
        temp.getFullYear(),
        temp.getMonth(),
        temp.getDate(),
        temp.getHours(),
        temp.getMinutes(),
        temp.getSeconds()
      )
    )
    return moment(convertedUTCDate).local().toDate()
  }

  function setInitialFormValue() {
    const defaultValues = menuFields?.reduce((acc, field) => {
      if (field.label === "unique_facts" || field.label === "glossary") {
        acc[field.label] = [{ fact: "" }]
      } else if (
        field.label === "allergens" ||
        field.label === "menu_description" ||
        field.label === "tasting_notes" ||
        field.label === "allergance"
      ) {
        acc[field.label] = []
      } else if (field.label === "ingredients") {
        acc[field.label] = [{ name: "", measurement: "" }]
      } else {
        acc[field.label] = ""
      }
      return acc
    }, {})

    const ingredientObjects =
      typeof foodItem?.ingredients === "string" &&
      foodItem?.ingredients?.split(",").map((ingredient) => ({
        name: ingredient.trim(),
        measurement: ""
      }))

    return menuFields?.reduce((acc, field) => {
      if (!isEdit || !foodItem.id) {
        // Return default values if not editing or no foodItem ID
        defaultValues.did_you_know = []

        defaultValues.item_history = history_initial_values
        defaultValues.soundbite_id = ""
        defaultValues.soundbite = ""
        defaultValues.soundbite_voice_id = ""
        return defaultValues
      }

      defaultValues.did_you_know =
        foodItem?.did_you_know?.length > 0
          ? [
              ...foodItem.did_you_know.map((item) => ({
                heading: item?.title ?? "",
                content: item?.description ?? "",
                image: item?.image ?? ""
              }))
            ]
          : []

      defaultValues.item_history = foodItem?.item_history
        ? {
            heading: foodItem?.item_history?.title ?? "",
            content: foodItem?.item_history?.description ?? ""
          }
        : history_initial_values

      defaultValues.soundbite_id = foodItem?.soundbite ?? ""
      defaultValues.soundbite = foodItem?.soundbite_info ?? ""
      defaultValues.soundbite_url = foodItem?.soundbite_url ?? ""
      defaultValues.soundbite_voice_id = foodItem?.voice_id ?? ""

      switch (field.label) {
        case "image":
          acc[field.label] = foodItem?.image
            ? { id: foodItem?.image_id, url: foodItem?.image ?? "" }
            : ""
          break
        case "tasting_notes":
          acc[field.label] = getTestingNotes(foodItem?.tasting_notes ?? [])
          break
        case "notification_time":
          acc[field.label] = foodItem?.notification_time
            ? moment(
                initDateConvert(foodItem.notification_date, foodItem.notification_time)
              ).format("HH:mm:ss")
            : ""
          break
        case "notification_date":
          acc[field.label] = foodItem?.notification_date
            ? moment(
                initDateConvert(foodItem.notification_date, foodItem.notification_time)
              ).format("MMM DD YYYY")
            : null
          break
        case "menu_description":
          acc[field.label] = getMenuDescription(foodItem?.menu_description)
          break
        case "allergance":
          acc[field.label] = getAllergence(foodItem?.allergance ?? [])
          break
        case "allergens":
          acc[field.label] = getAllergence(foodItem?.allergens ?? [])
          break
        case "unique_facts":
          acc[field.label] =
            foodItem?.unique_facts?.length > 0 ? foodItem?.unique_facts : [{ fact: "" }]
          break
        case "glossary":
          acc[field.label] = foodItem?.glossary?.length > 0 ? foodItem?.glossary : [{ fact: "" }]
          break
        case "ingredients":
          acc[field.label] =
            typeof foodItem?.ingredients === "string"
              ? ingredientObjects
              : Array.isArray(foodItem?.ingredients)
              ? foodItem.ingredients
              : [{ name: "", measurement: "" }]
          break
        default:
          acc[field.label] = foodItem?.[field.label] || ""
          break
      }

      return acc
    }, defaultValues)
  }

  function setInitialBarFormValue() {
    const defaultValues = menuFields?.reduce((acc, field) => {
      if (field.label === "unique_facts" || field.label === "glossary") {
        acc[field.label] = [{ fact: "" }]
      } else if (
        field.label === "allergens" ||
        field.label === "menu_description" ||
        field.label === "tasting_notes" ||
        field.label === "allergance" ||
        field.label === "ice"
      ) {
        acc[field.label] = []
      } else if (field.label === "ingredients") {
        acc[field.label] = [{ name: "", measurement: "" }]
      } else {
        acc[field.label] = ""
      }
      return acc
    }, {})
    let notificationTime = ""
    let notificationDate = ""

    if (foodItem?.notification_date && foodItem?.notification_time) {
      const notificationTimeStamp = initDateConvert(
        foodItem.notification_date,
        foodItem.notification_time
      )
      notificationTime = moment(notificationTimeStamp).format("HH:mm:ss")
      notificationDate = moment(notificationTimeStamp).format("MMM DD YYYY")
    }

    const ingredientObjects =
      typeof foodItem?.ingredients === "string" &&
      foodItem?.ingredients?.split(",").map((ingredient) => ({
        name: ingredient.trim(),
        measurement: ""
      }))

    return menuFields?.reduce((acc, field) => {
      if (!isEdit || !foodItem.id) {
        // Return default values if not editing or no foodItem ID
        defaultValues.did_you_know = []

        defaultValues.item_history = history_initial_values
        defaultValues.soundbite_id = ""
        defaultValues.soundbite = ""
        defaultValues.soundbite_voice_id = ""
        return defaultValues
      }

      defaultValues.did_you_know = defaultValues.did_you_know =
        foodItem?.did_you_know?.length > 0
          ? [
              ...foodItem.did_you_know.map((item) => ({
                heading: item?.title ?? "",
                content: item?.description ?? "",
                image: item?.image ?? ""
              }))
            ]
          : []

      defaultValues.item_history = foodItem?.item_history
        ? {
            heading: foodItem?.item_history?.title ?? "",
            content: foodItem?.item_history?.description ?? ""
          }
        : history_initial_values

      defaultValues.soundbite_id = foodItem?.soundbite ?? ""
      defaultValues.soundbite = foodItem?.soundbite_info ?? ""
      defaultValues.soundbite_url = foodItem?.soundbite_url ?? ""
      defaultValues.soundbite_voice_id = foodItem?.voice_id ?? ""

      switch (field.label) {
        case "image":
          acc[field.label] = foodItem?.image
            ? { id: foodItem?.image, url: foodItem?.image_url ?? "" }
            : ""
          break
        case "menu_description":
          acc[field.label] = getMenuDescription(foodItem?.menu_description)
          break
        case "allergance":
          acc[field.label] = getAllergence(foodItem?.allergance ?? [])
          break
        case "ice":
          acc[field.label] = getAllergence(foodItem?.ice ?? [])
          break
        case "allergens":
          acc[field.label] = getAllergence(foodItem?.allergens ?? [])
          break
        case "notification_time":
          acc[field.label] = foodItem?.notification_time ? notificationTime : ""
          break
        case "notification_date":
          acc[field.label] = foodItem?.notification_date ? notificationDate : null
          break
        case "unique_facts":
          acc[field.label] =
            foodItem?.unique_facts?.length > 0 ? foodItem?.unique_facts : [{ fact: "" }]
          break
        case "glossary":
          acc[field.label] = foodItem?.glossary?.length > 0 ? foodItem?.glossary : [{ fact: "" }]
          break
        case "ingredients":
          acc[field.label] =
            typeof foodItem?.ingredients === "string"
              ? ingredientObjects
              : Array.isArray(foodItem?.ingredients)
              ? foodItem.ingredients
              : [{ name: "", measurement: "" }]
          break
        case "tasting_notes":
          acc[field.label] = getTestingNotes(foodItem?.tasting_notes ?? [])
          break
        default:
          acc[field.label] = foodItem?.[field.label] || ""
          break
      }

      return acc
    }, defaultValues)
  }

  function setInitialBeerFormValue() {
    const defaultValues = menuFields?.reduce((acc, field) => {
      if (field.label === "tasting_notes") {
        acc[field.label] = []
      } else if (field.label === "unique_facts" || field.label === "glossary") {
        acc[field.label] = [{ fact: "" }]
      } else if (
        field.label === "allergens" ||
        field.label === "menu_description" ||
        field.label === "tasting_notes" ||
        field.label === "allergance" ||
        field.label === "ice"
      ) {
        acc[field.label] = []
      } else if (field.label === "ingredients") {
        acc[field.label] = [{ name: "", measurement: "" }]
      } else {
        acc[field.label] = ""
      }
      return acc
    }, {})
    let notificationTime = ""
    let notificationDate = ""

    if (foodItem?.notification_date && foodItem?.notification_time) {
      const notificationTimeStamp = initDateConvert(
        foodItem.notification_date,
        foodItem.notification_time
      )
      notificationTime = moment(notificationTimeStamp).format("HH:mm:ss")
      notificationDate = moment(notificationTimeStamp).format("MMM DD YYYY")
    }

    const ingredientObjects =
      typeof foodItem?.ingredients === "string" &&
      foodItem?.ingredients?.split(",").map((ingredient) => ({
        name: ingredient.trim(),
        measurement: ""
      }))

    return menuFields?.reduce((acc, field) => {
      if (!isEdit || !foodItem.id) {
        // Return default values if not editing or no foodItem ID
        defaultValues.did_you_know = []

        defaultValues.item_history = history_initial_values
        defaultValues.soundbite_id = ""
        defaultValues.soundbite = ""
        defaultValues.soundbite_voice_id = ""
        return defaultValues
      }

      defaultValues.did_you_know = defaultValues.did_you_know =
        foodItem?.did_you_know?.length > 0
          ? [
              ...foodItem.did_you_know.map((item) => ({
                heading: item?.title ?? "",
                content: item?.description ?? "",
                image: item?.image ?? ""
              }))
            ]
          : []

      defaultValues.item_history = foodItem?.item_history
        ? {
            heading: foodItem?.item_history?.title ?? "",
            content: foodItem?.item_history?.description ?? ""
          }
        : history_initial_values

      defaultValues.soundbite_id = foodItem?.soundbite ?? ""
      defaultValues.soundbite = foodItem?.soundbite_info ?? ""
      defaultValues.soundbite_url = foodItem?.soundbite_url ?? ""
      defaultValues.soundbite_voice_id = foodItem?.voice_id ?? ""

      switch (field.label) {
        case "image":
          acc[field.label] = foodItem?.image
            ? { id: foodItem?.image, url: foodItem?.image_url ?? "" }
            : ""
          break
        case "tasting_notes":
          acc[field.label] = getTestingNotes(foodItem?.tasting_notes ?? [])
          break
        case "notification_time":
          acc[field.label] = foodItem?.notification_time ? notificationTime : ""
          break
        case "notification_date":
          acc[field.label] = foodItem?.notification_date ? notificationDate : null
          break
        case "menu_description":
          acc[field.label] = getMenuDescription(foodItem?.menu_description)
          break
        case "ice":
          acc[field.label] = getAllergence(foodItem?.ice ?? [])
          break
        case "allergance":
          acc[field.label] = getAllergence(foodItem?.allergance ?? [])
          break
        case "allergens":
          acc[field.label] = getAllergence(foodItem?.allergens ?? [])
          break
        case "unique_facts":
          acc[field.label] =
            foodItem?.unique_facts?.length > 0 ? foodItem?.unique_facts : [{ fact: "" }]
          break
        case "glossary":
          acc[field.label] = foodItem?.glossary?.length > 0 ? foodItem?.glossary : [{ fact: "" }]
          break
        case "ingredients":
          acc[field.label] =
            typeof foodItem?.ingredients === "string"
              ? ingredientObjects
              : Array.isArray(foodItem?.ingredients)
              ? foodItem.ingredients
              : [{ name: "", measurement: "" }]
          break
        case "abv":
          acc[field.label] = foodItem?.abv !== "" ? foodItem?.abv : ""
          break
        default:
          acc[field.label] = foodItem?.[field.label] || ""
          break
      }

      return acc
    }, defaultValues)
  }

  function setInitialWineFormValue() {
    const defaultValues = menuFields?.reduce((acc, field) => {
      if (field.label === "unique_facts" || field.label === "glossary") {
        acc[field.label] = [{ fact: "" }]
      } else if (
        field.label === "allergens" ||
        field.label === "menu_description" ||
        field.label === "tasting_notes" ||
        field.label === "allergance"
      ) {
        acc[field.label] = []
      } else if (field.label === "ingredients") {
        acc[field.label] = [{ name: "", measurement: "" }]
      } else {
        acc[field.label] = ""
      }
      return acc
    }, {})

    let notificationTime = ""
    let notificationDate = ""

    if (foodItem?.notification_date && foodItem?.notification_time) {
      const notificationTimeStamp = initDateConvert(
        foodItem.notification_date,
        foodItem.notification_time
      )
      notificationTime = moment(notificationTimeStamp).format("HH:mm:ss")
      notificationDate = moment(notificationTimeStamp).format("MMM DD YYYY")
    }

    const ingredientObjects =
      typeof foodItem?.ingredients === "string" &&
      foodItem?.ingredients?.split(",").map((ingredient) => ({
        name: ingredient.trim(),
        measurement: ""
      }))

    return menuFields?.reduce((acc, field) => {
      if (!isEdit || !foodItem.id) {
        // Return default values if not editing or no foodItem ID
        defaultValues.did_you_know = []

        defaultValues.item_history = history_initial_values
        defaultValues.soundbite_id = ""
        defaultValues.soundbite = ""
        defaultValues.soundbite_voice_id = ""
        return defaultValues
      }

      defaultValues.did_you_know = defaultValues.did_you_know =
        foodItem?.did_you_know?.length > 0
          ? [
              ...foodItem.did_you_know.map((item) => ({
                heading: item?.title ?? "",
                content: item?.description ?? "",
                image: item?.image ?? ""
              }))
            ]
          : []

      defaultValues.item_history = foodItem?.item_history
        ? {
            heading: foodItem?.item_history?.title ?? "",
            content: foodItem?.item_history?.description ?? ""
          }
        : history_initial_values

      defaultValues.soundbite_id = foodItem?.soundbite ?? ""
      defaultValues.soundbite = foodItem?.soundbite_info ?? ""
      defaultValues.soundbite_url = foodItem?.soundbite_url ?? ""
      defaultValues.soundbite_voice_id = foodItem?.voice_id ?? ""

      switch (field.label) {
        case "image":
          acc[field.label] = foodItem?.image
            ? { id: foodItem?.image, url: foodItem?.image_url ?? "" }
            : ""
          break
        case "tasting_notes":
          acc[field.label] = getTestingNotes(foodItem?.tasting_notes ?? [])
          break
        case "notification_time":
          acc[field.label] = foodItem?.notification_time ? notificationTime : ""
          break
        case "notification_date":
          acc[field.label] = foodItem?.notification_date ? notificationDate : null
          break
        case "menu_description":
          acc[field.label] = getMenuDescription(foodItem?.menu_description)
          break
        case "allergance":
          acc[field.label] = getAllergence(foodItem?.allergance ?? [])
          break
        case "allergens":
          acc[field.label] = getAllergence(foodItem?.allergens ?? [])
          break
        case "unique_facts":
          acc[field.label] =
            foodItem?.unique_facts?.length > 0 ? foodItem?.unique_facts : [{ fact: "" }]
          break
        case "glossary":
          acc[field.label] = foodItem?.glossary?.length > 0 ? foodItem?.glossary : [{ fact: "" }]
          break
        case "ingredients":
          acc[field.label] =
            typeof foodItem?.ingredients === "string"
              ? ingredientObjects
              : Array.isArray(foodItem?.ingredients)
              ? foodItem.ingredients
              : [{ name: "", measurement: "" }]
          break
        default:
          acc[field.label] = foodItem?.[field.label] || ""
          break
      }

      return acc
    }, defaultValues)
  }

  useEffect(() => {
    dispatch(marketingActions.setMenuImageId(""))
  }, [])

  function convertDate(date, time) {
    const forDate = moment(new Date(date)).format("ddd MMM DD YYYY") + " " + time
    const newDate = new Date(forDate)
    return newDate.toUTCString()
  }
  function payloadDate(dateArray = []) {
    return `${dateArray[3]}-${months[dateArray[2]]}-${dateArray[1]}`
  }

  function handleSubmit(e) {
    let payloadStartDate = null
    let NotificationTimeSplit = null
    if (e.notification_date && e.notification_time) {
      const NotificationTimeStamp = convertDate(e.notification_date, e.notification_time)
      NotificationTimeSplit = NotificationTimeStamp.split(" ")
      payloadStartDate = payloadDate(NotificationTimeSplit)
    }

    const dynamicPayload = menuFields.reduce((acc, field) => {
      const fieldValue = (() => {
        switch (field.label) {
          case "notification_date":
            return payloadStartDate || null
          case "notification_time":
            return NotificationTimeSplit ? NotificationTimeSplit[4] : null
          case "allergance":
            return isEdit === false && !e.id
              ? e.allergance
              : e.allergance?.flatMap((items) => items)
          case "allergens":
            return isEdit === false && !e.id ? e.allergens : e.allergens?.flatMap((items) => items)
          case "item_price":
            return e.item_price !== "" ? e.item_price : null
          case "price":
            return e.price !== "" ? e.price : null
          case "image":
            return menuItemImageId ? menuItemImageId : e.image ? e?.image?.id : ""
          case "unique_facts":
            return e?.unique_facts ? e?.unique_facts.filter((fact) => fact.fact.trim() !== "") : ""
          case "glossary":
            return e?.glossary ? e?.glossary.filter((fact) => fact.fact.trim() !== "") : ""
          case "ingredients":
            return e?.ingredients ? e?.ingredients : []
          default:
            return e?.[field.label]
        }
      })()

      acc[field.label] = fieldValue
      return acc
    }, {})

    const payload = {
      ...dynamicPayload,
      meal_period: linkCategory?.item,
      category: categoryId ?? menu_Category_ID,
      time_zone: zone_name,
      did_you_know: getDidYouKnowPayload(e.did_you_know, didYouKnowImage),
      item_history: getHistoryPayload(e.item_history),
      soundbite: e?.soundbite !== "" ? (soundbites?.id ? soundbites?.id : e?.soundbite_id) : "",
      soundbite_info: e?.soundbite || "",
      voice_id: e?.soundbite_voice_id || ""
    }

    const action = !isEdit && !foodItem.id ? addMenuItemsActions : updateFoodItemsAction
    dispatch(action({ payload, ...buildSaveOpts(), pending_review: isReviewMode }))
  }

  function handleSubmitWineItem(e) {
    let payloadStartDate = null
    let NotificationTimeSplit = null
    if (e.notification_date && e.notification_time) {
      const NotificationTimeStamp = convertDate(e.notification_date, e.notification_time)
      NotificationTimeSplit = NotificationTimeStamp.split(" ")
      payloadStartDate = payloadDate(NotificationTimeSplit)
    }

    const dynamicPayload = menuFields.reduce((acc, field) => {
      const fieldValue = (() => {
        switch (field.label) {
          case "notification_date":
            return payloadStartDate || null
          case "notification_time":
            return NotificationTimeSplit ? NotificationTimeSplit[4] : null
          case "item_price":
            return e.item_price !== "" ? e.item_price : null
          case "price":
            return e.price !== "" ? e.price : null
          case "image":
            return menuItemImageId ? menuItemImageId : e.image ? e?.image?.id : ""
          case "allergance":
            return isEdit === false && !e.id
              ? e.allergance
              : e.allergance?.flatMap((items) => items)
          case "allergens":
            return isEdit === false && !e.id ? e.allergens : e.allergens?.flatMap((items) => items)
          case "unique_facts":
            return e?.unique_facts ? e?.unique_facts.filter((fact) => fact.fact.trim() !== "") : ""
          case "glossary":
            return e?.glossary ? e?.glossary.filter((fact) => fact.fact.trim() !== "") : ""
          case "ingredients":
            return e?.ingredients ? e?.ingredients : []
          case "ice":
            return e?.ice ? e?.ice : []
          default:
            return e?.[field.label]
        }
      })()

      acc[field.label] = fieldValue
      return acc
    }, {})

    const payload = {
      ...dynamicPayload,
      meal_period: linkCategory?.item,
      time_zone: zone_name,
      sub_category: menu_Category_ID,
      did_you_know: getDidYouKnowPayload(e.did_you_know, didYouKnowImage),
      item_history: getHistoryPayload(e.item_history),
      soundbite: e?.soundbite !== "" ? (soundbites?.id ? soundbites?.id : e?.soundbite_id) : "",
      soundbite_info: e?.soundbite || "",
      voice_id: e?.soundbite_voice_id || ""
    }

    if (isEdit === false && !foodItem.id) {
      dispatch(
        addWineMenuItemsAction({
          payload: payload,
          ...buildSaveOpts(),
          pending_review: isReviewMode
        })
      )
    } else {
      dispatch(
        updateWineItemsAction({
          payload: payload,
          ...buildSaveOpts(),
          pending_review: isReviewMode
        })
      )
    }
  }

  function handleSubmitBarItem(e) {
    let payloadStartDate = null
    let NotificationTimeSplit = null
    if (e.notification_date && e.notification_time) {
      const NotificationTimeStamp = convertDate(e.notification_date, e.notification_time)
      NotificationTimeSplit = NotificationTimeStamp.split(" ")
      payloadStartDate = payloadDate(NotificationTimeSplit)
    }

    const dynamicPayload = menuFields.reduce((acc, field) => {
      const fieldValue = (() => {
        switch (field.label) {
          case "notification_date":
            return payloadStartDate || null
          case "notification_time":
            return NotificationTimeSplit ? NotificationTimeSplit[4] : null
          case "item_price":
            return e.item_price !== "" ? e.item_price : null
          case "price":
            return e.price !== "" ? e.price : null
          case "image":
            return menuItemImageId ? menuItemImageId : e.image ? e?.image?.id : ""
          case "unique_facts":
            return e?.unique_facts ? e?.unique_facts.filter((fact) => fact.fact.trim() !== "") : ""
          case "glossary":
            return e?.glossary ? e?.glossary.filter((fact) => fact.fact.trim() !== "") : ""
          case "allergance":
            return isEdit === false && !e.id
              ? e.allergance
              : e.allergance?.flatMap((items) => items)
          case "allergens":
            return isEdit === false && !e.id ? e.allergens : e.allergens?.flatMap((items) => items)
          case "ingredients":
            return e?.ingredients ? e?.ingredients : []
          case "ice":
            return e?.ice ? e?.ice : []
          default:
            return e?.[field.label]
        }
      })()

      acc[field.label] = fieldValue
      return acc
    }, {})

    const payload = {
      ...dynamicPayload,
      meal_period: "bar menu",
      bar_category: menu_Category_ID,
      time_zone: zone_name,
      did_you_know: getDidYouKnowPayload(e.did_you_know, didYouKnowImage),
      item_history: getHistoryPayload(e.item_history),
      soundbite: e?.soundbite !== "" ? (soundbites?.id ? soundbites?.id : e?.soundbite_id) : "",
      soundbite_info: e?.soundbite || "",
      voice_id: e?.soundbite_voice_id || ""
    }
    if (isEdit === false && !foodItem.id) {
      dispatch(
        addBarMenuItemsAction({
          payload: payload,
          ...buildSaveOpts(),
          pending_review: isReviewMode
        })
      )
    } else {
      dispatch(
        updateBarItemsAction({
          payload: payload,
          ...buildSaveOpts(),
          pending_review: isReviewMode
        })
      )
    }
  }

  function handleSubmitBeerItem(e) {
    let payloadStartDate = null
    let NotificationTimeSplit = null

    if (e.notification_date && e.notification_time) {
      const NotificationTimeStamp = convertDate(e.notification_date, e.notification_time)
      NotificationTimeSplit = NotificationTimeStamp.split(" ")
      payloadStartDate = payloadDate(NotificationTimeSplit)
    }

    const dynamicPayload = menuFields.reduce((acc, field) => {
      const fieldValue = (() => {
        switch (field.label) {
          case "notification_date":
            return payloadStartDate || null
          case "notification_time":
            return NotificationTimeSplit ? NotificationTimeSplit[4] : null
          case "item_price":
            return e.item_price !== "" ? e.item_price : null
          case "price":
            return e.price !== "" ? e.price : null
          case "abv":
            return e.abv !== "" ? e.abv : null
          case "image":
            return menuItemImageId ? menuItemImageId : e.image ? e?.image?.id : ""
          case "unique_facts":
            return e?.unique_facts ? e?.unique_facts.filter((fact) => fact.fact.trim() !== "") : ""
          case "glossary":
            return e?.glossary ? e?.glossary.filter((fact) => fact.fact.trim() !== "") : ""
          case "allergance":
            return isEdit === false && !e.id
              ? e.allergance
              : e.allergance?.flatMap((items) => items)
          case "allergens":
            return isEdit === false && !e.id ? e.allergens : e.allergens?.flatMap((items) => items)
          case "ingredients":
            return e?.ingredients ? e?.ingredients : []
          case "ice":
            return e?.ice ? e?.ice : []
          default:
            return e?.[field.label]
        }
      })()

      acc[field.label] = fieldValue
      return acc
    }, {})

    const payload = {
      ...dynamicPayload,
      meal_period: "bar menu",
      bar_category: menu_Category_ID,
      time_zone: zone_name,
      did_you_know: getDidYouKnowPayload(e.did_you_know, didYouKnowImage),
      item_history: getHistoryPayload(e.item_history),
      soundbite: e?.soundbite !== "" ? (soundbites?.id ? soundbites?.id : e?.soundbite_id) : "",
      soundbite_info: e?.soundbite || "",
      voice_id: e?.soundbite_voice_id || ""
    }
    if (isEdit === false && !foodItem.id) {
      dispatch(
        addBarMenuItemsAction({
          payload: payload,
          ...buildSaveOpts(),
          pending_review: isReviewMode
        })
      )
    } else {
      dispatch(
        updateBarItemsAction({
          payload: payload,
          ...buildSaveOpts(),
          pending_review: isReviewMode
        })
      )
    }
  }

  useEffect(() => {
    if (open && linkCategory?.item === menuText.BARMENU) {
      dispatch(
        getDynamicMenuFieldsAction({
          category: toTitleCase(linkCategory?.item),
          sub_category: bar_category
        })
      )
    }
  }, [linkCategory?.item, bar_category, open])

  function createDyanmicValidation(fields, item) {
    const dynamiValidation = generateDynamicValidations(fields, item)
    return dynamiValidation
  }

  return (
    <>
      {linkCategory?.item !== menuText.BARMENU &&
        linkCategory?.item !== menuText.WINEBAR &&
        linkCategory?.item !== menuText.WINEBOTTLE && (
          <Formik
            initialValues={setInitialFormValue()}
            enableReinitialize={true}
            validationSchema={createDyanmicValidation(menuFields, "Dish")}
            onSubmit={handleSubmit}>
            {(formik) => (
              <MenuItemsForm
                isEdit={isEdit}
                onClose={onClose}
                formik={formik}
                saveBeforeAi={saveBeforeAi}
                open={open}
                title={`${toTitleCase(linkCategory?.item)} Item`}
                menuFields={menuFields}
              />
            )}
          </Formik>
        )}
      {linkCategory?.item === menuText.BARMENU &&
        (bar_category?.toLowerCase() !== "beer" ? (
          <Formik
            initialValues={setInitialBarFormValue()}
            enableReinitialize={true}
            validationSchema={createDyanmicValidation(menuFields, "Cocktail")}
            onSubmit={handleSubmitBarItem}>
            {(formik) => (
              <>
                <MenuItemsForm
                  isEdit={isEdit}
                  onClose={onClose}
                  formik={formik}
                  saveBeforeAi={saveBeforeAi}
                  open={open}
                  title="Bar Item"
                  menuFields={menuFields}
                />
              </>
            )}
          </Formik>
        ) : (
          <Formik
            initialValues={setInitialBeerFormValue()}
            enableReinitialize={true}
            validationSchema={createDyanmicValidation(menuFields, "Beer")}
            onSubmit={handleSubmitBeerItem}>
            {(formik) => (
              <>
                <MenuItemsForm
                  isEdit={isEdit}
                  onClose={onClose}
                  formik={formik}
                  saveBeforeAi={saveBeforeAi}
                  open={open}
                  title="Beer Item"
                  menuFields={menuFields}
                />
              </>
            )}
          </Formik>
        ))}
      {(linkCategory?.item === menuText.WINEBAR || linkCategory?.item === menuText.WINEBOTTLE) && (
        <Formik
          initialValues={setInitialWineFormValue()}
          enableReinitialize={true}
          validationSchema={createDyanmicValidation(menuFields, "Wine")}
          onSubmit={handleSubmitWineItem}>
          {(formik) => (
            <>
              <MenuItemsForm
                isEdit={isEdit}
                onClose={onClose}
                formik={formik}
                saveBeforeAi={saveBeforeAi}
                open={open}
                title="Wine Item"
                menuFields={menuFields}
              />
            </>
          )}
        </Formik>
      )}
    </>
  )
}

export default AddMenuItems
const months = {
  Jan: "01",
  Feb: "02",
  Mar: "03",
  Apr: "04",
  May: "05",
  Jun: "06",
  Jul: "07",
  Aug: "08",
  Sep: "09",
  Oct: "10",
  Nov: "11",
  Dec: "12"
}
